import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { useSEO } from '@/hooks/useSEO';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  useSEO({
    title: article ? `${article.title} - 一品楼论坛` : '文章未找到 - 一品楼论坛',
    description: article?.excerpt || '一品楼论坛文章详情',
    canonicalUrl: article ? `https://wouzfuo.cn/article/${article.id}` : undefined,
  });

  if (!article) {
    return (
      <>
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
                <i className="ri-file-unknow-line text-3xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-foreground-900 mb-3 font-heading">文章未找到</h1>
              <p className="text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被删除。</p>
              <Link
                to="/"
                className="inline-flex px-5 py-2.5 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://wouzfuo.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://wouzfuo.cn/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.avatar,
    publisher: {
      '@type': 'Organization',
      name: '一品楼论坛',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://wouzfuo.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article className="max-w-3xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={breadcrumbItems} />

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                {article.isHot && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                    <i className="ri-fire-line"></i>热门
                  </span>
                )}
                {article.isPinned && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                    <i className="ri-pushpin-line"></i>置顶
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 font-heading leading-snug">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <img
                    src={article.avatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                    width="32"
                    height="32"
                  />
                  <span className="text-sm font-medium text-foreground-800">{article.author}</span>
                </div>
                <span className="text-sm text-foreground-500">{article.publishDate}</span>
                <span className="text-sm text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views >= 1000 ? `${(article.views / 1000).toFixed(1)}k` : article.views} 阅读
                </span>
              </div>
            </header>

            <div
              className="prose prose-lg max-w-none mb-10 text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border border-background-200/70"
                >
                  <i className="ri-price-tag-3-line mr-1"></i>
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-4 border-t border-background-200/70 mb-10">
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer">
                <i className="ri-thumb-up-line"></i>
                <span>赞 {article.likes}</span>
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer">
                <i className="ri-chat-3-line"></i>
                <span>评论 {article.comments}</span>
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer">
                <i className="ri-bookmark-line"></i>
                <span>收藏</span>
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer">
                <i className="ri-share-forward-line"></i>
                <span>分享</span>
              </button>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100/70 py-10">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <h2 className="text-lg font-bold text-foreground-900 mb-5 font-heading">相关推荐</h2>
                <div className="flex flex-col gap-3">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} variant="default" />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}